import { useMemo } from "react";
import { CalendarRange, ExternalLink, Layers, Plus, Trophy } from "lucide-react";
import { Button } from "../ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { parseLocalDate } from "../../lib/programBuilderUtils";
import { useReadableColor } from "../../lib/useChartColors";
import {
	buildSeasonSegments,
	competitionsForRange,
	daysBetween,
	findNextCompetition,
	getWeekCalendarRange,
	getWeekCalendarRanges,
	getWeekDateRange,
	mesocyclesForRange,
} from "../../lib/seasonAgenda";

/**
 * Season strip: the builder's week tabs with the athlete's mesocycle phases
 * drawn above them and a race marker on the weeks that hold a competition
 * (docs/program-builder-season-strip/ANALYSIS.md §3).
 *
 * Layout is a CSS grid with one column per week so the phase band aligns with
 * the tabs without measuring anything: tabs sit on the last row, each phase
 * segment spans `startIndex+1 / span N` on the first row. When `agenda` is
 * null the component renders the tabs exactly as the builder always did.
 */

const shortDate = (iso, language) =>
	parseLocalDate(iso).toLocaleDateString(language === "it" ? "it-IT" : "en-US", {
		day: "numeric",
		month: "short",
	});

const formatRange = (range, language) => {
	if (!range) return "";
	if (range.start === range.end) return shortDate(range.start, language);
	return `${shortDate(range.start, language)} – ${shortDate(range.end, language)}`;
};

const formatCountdown = (days, t) => {
	if (days === 0) return t("pbSeasonToday");
	if (days === 1) return t("pbSeasonTomorrow");
	if (days < 0) return t("pbSeasonOngoing");
	return `${t("pbSeasonIn")} ${days} ${t("pbSeasonDaysShort")}`;
};

const competitionLabel = (cluster, athleteCount, t) => {
	const parts = [cluster.name];
	if (cluster.priority) parts.push(`(${cluster.priority})`);
	if (athleteCount > 1 && cluster.athleteIds.length > 0) {
		parts.push(`· ${cluster.athleteIds.length}/${athleteCount} ${t("pbSeasonAthletes")}`);
	}
	return parts.join(" ");
};

export function SeasonStrip({
	weeks,
	selectedWeekIndex,
	onSelectWeek,
	selectedTabRef,
	weekStatusClass,
	weekStatusLabel,
	onAddWeek,
	agenda,
	onPlan,
	onRetryAgenda,
	t,
	language,
}) {
	const readable = useReadableColor();
	const weekList = useMemo(() => weeks || [], [weeks]);
	const showPhases = Boolean(agenda?.showPhases);
	const segments = useMemo(
		() => (showPhases ? buildSeasonSegments(weekList, agenda?.mesocycles) : []),
		[showPhases, weekList, agenda?.mesocycles],
	);
	// Two ranges per week: the days it actually holds (what we print) and the
	// calendar weeks it occupies (what races and phases are matched against, so
	// a Sunday race on a Mon-Fri week still lands here — and a week with no day
	// yet sits where its first day will go).
	const weekRanges = useMemo(() => weekList.map(getWeekDateRange), [weekList]);
	const weekMatchRanges = useMemo(
		() => getWeekCalendarRanges(weekList),
		[weekList],
	);

	// With phases: row 1 caption ("Mesocycle planning · athlete"), row 2 the
	// phase band, row 3 the tabs. Without: tabs only, exactly as before.
	const bandRow = 2;
	const tabRow = showPhases ? 3 : 1;
	const columns = weekList.length + 1;

	return (
		<div className="overflow-x-auto pb-1" data-testid="season-strip">
			<div
				className="grid items-end gap-x-2 gap-y-1"
				style={{ gridTemplateColumns: `repeat(${columns}, max-content)` }}
			>
				{showPhases && weekList.length > 0 && (
					<div
						style={{ gridRow: 1, gridColumn: `1 / span ${columns}` }}
						className="hidden md:flex items-center gap-1.5 text-[10px] font-heading uppercase tracking-wider text-muted-foreground min-w-0"
						data-testid="season-caption"
					>
						<CalendarRange className="w-3 h-3 shrink-0" />
						<span className="truncate">
							{t("pbSeasonBandLabel")}
							{agenda?.subjectName ? ` · ${agenda.subjectName}` : ""}
						</span>
					</div>
				)}

				{showPhases &&
					segments.map((segment) => {
						const { mesocycle, startIndex, span } = segment;
						/* `readable` scurisce il colore scelto dal coach quanto basta
						   a leggerlo su fondo chiaro; nel tema scuro lo lascia com'e'. */
						const color = readable(mesocycle.color || "#007AFF");
						const phaseDates = formatRange(
							{
								start: mesocycle.start_date,
								end: mesocycle.end_date || mesocycle.start_date,
							},
							language,
						);
						const title = [mesocycle.name, mesocycle.description, phaseDates]
							.filter(Boolean)
							.join("\n");
						return (
							<button
								key={`${mesocycle.mesocycle_id}-${startIndex}`}
								type="button"
								onClick={() => onSelectWeek(startIndex)}
								title={title}
								style={{
									gridRow: bandRow,
									gridColumn: `${startIndex + 1} / span ${span}`,
									backgroundColor: `${color}26`,
									borderColor: `${color}80`,
									color,
								}}
								className="h-[3px] md:h-5 min-w-0 rounded-sm border px-1.5 text-left overflow-hidden"
								data-testid="season-phase"
							>
								{/* Dates sit after the name so a one-week segment still
								    shows the name and only truncates the dates away. */}
								<span className="hidden md:flex items-baseline gap-1.5 text-[10px] font-heading uppercase tracking-wider leading-[18px] min-w-0">
									<span className="truncate">{mesocycle.name}</span>
									<span className="truncate font-mono normal-case tracking-normal opacity-70">
										{phaseDates}
									</span>
								</span>
							</button>
						);
					})}

				{showPhases && segments.length === 0 && weekList.length > 0 && (
					<button
						type="button"
						onClick={agenda.failed ? onRetryAgenda : onPlan}
						style={{ gridRow: bandRow, gridColumn: `1 / span ${weekList.length}` }}
						className="hidden md:flex h-5 items-center gap-1.5 rounded-sm border border-dashed border-border px-2 text-[10px] font-heading uppercase tracking-wider text-muted-foreground hover:text-volt hover:border-volt/50"
						data-testid="season-empty"
					>
						{agenda.failed ? t("pbSeasonAgendaError") : t("pbSeasonNoPhases")}
						<span className="text-volt">
							· {agenda.failed ? t("pbSeasonAgendaRetry") : t("pbSeasonPlan")}
						</span>
					</button>
				)}

				{weekList.map((week, wi) => {
					const weekCompetitions = agenda
						? competitionsForRange(agenda.competitions, weekMatchRanges[wi])
						: [];
					const weekPhases =
						showPhases && weekMatchRanges[wi]
							? mesocyclesForRange(agenda.mesocycles, weekMatchRanges[wi])
							: [];
					const hasRace = weekCompetitions.length > 0;
					const hasPriorityRace = weekCompetitions.some((c) => c.priority === "A");
					// What athletes see of the week, as text: the dot below is colour
					// only, so the sentence goes in its aria-label and in the tab title.
					const statusLabel =
						typeof weekStatusLabel === "function"
							? weekStatusLabel(week, wi) || ""
							: "";
					const agendaLines = agenda
						? [
								formatRange(weekRanges[wi], language),
								...weekPhases.map((p) => p.name),
								...weekCompetitions.map(
									(c) => `🏆 ${competitionLabel(c, agenda?.athleteCount, t)}`,
								),
							]
						: [];
					const title =
						[statusLabel, ...agendaLines].filter(Boolean).join("\n") || undefined;
					const isSelected = selectedWeekIndex === wi;
					return (
						<button
							key={week.week_id}
							ref={isSelected ? selectedTabRef : null}
							onClick={() => onSelectWeek(wi)}
							title={title}
							style={{ gridRow: tabRow, gridColumn: wi + 1 }}
							className={`flex items-center gap-1.5 px-4 py-2 rounded-sm border text-sm font-heading whitespace-nowrap transition-colors ${
								isSelected
									? "border-volt bg-volt/10 text-volt"
									: "border-border text-muted-foreground hover:border-volt/50"
							}`}
						>
							<Layers className="w-3.5 h-3.5" />
							{t("weekShort")} {wi + 1}
							{hasRace && (
								<Trophy
									className={`w-3 h-3 shrink-0 ${
										hasPriorityRace ? "text-blaze" : "text-amber-400"
									}`}
									data-testid="season-race-marker"
								/>
							)}
							<span
								role={statusLabel ? "img" : undefined}
								aria-label={statusLabel || undefined}
								aria-hidden={statusLabel ? undefined : true}
								className={`w-2 h-2 rounded-full shrink-0 ${weekStatusClass(week, wi)}`}
							/>
						</button>
					);
				})}

				<Button
					variant="ghost"
					size="sm"
					style={{ gridRow: tabRow, gridColumn: columns }}
					className="rounded-sm border border-dashed border-border text-muted-foreground hover:text-volt hover:border-volt/50 whitespace-nowrap"
					onClick={onAddWeek}
				>
					<Plus className="w-3.5 h-3.5 mr-1" />
					{t("week")}
				</Button>
			</div>
		</div>
	);
}

/**
 * One-line context for the selected week: its dates, the phase it belongs to,
 * the next race with a countdown, the athlete whose agenda is shown (when the
 * program reaches more than one) and the way into planning.
 */
export function SeasonWeekContext({
	week,
	// Calendar range of `week` as the strip places it (`getWeekCalendarRanges`):
	// a week with no day yet only has one relative to the others.
	calendarRange,
	agenda,
	// Who the agenda belongs to: the athletes of an individual program or the
	// groups of a group program (same `{ user_id, name }` option shape).
	subjectOptions,
	selectedSubjectId,
	onSelectSubject,
	onPlan,
	t,
	language,
}) {
	const readable = useReadableColor();
	const range = getWeekDateRange(week);
	const matchRange =
		calendarRange === undefined ? getWeekCalendarRange(week) : calendarRange;
	const phases = agenda?.showPhases
		? mesocyclesForRange(agenda.mesocycles, matchRange)
		: [];
	const weekCompetitions = competitionsForRange(agenda?.competitions, matchRange);
	const anchor = matchRange?.start || null;
	const next = anchor ? findNextCompetition(agenda?.competitions, anchor) : null;
	const nextInWeek = next && weekCompetitions.some((c) => c.key === next.key);
	const daysToNext = next && anchor ? daysBetween(anchor, next.date) : null;
	const showSubjectPicker = (subjectOptions || []).length > 1 && agenda?.showPhases;

	return (
		<div
			className="flex items-center gap-1.5 flex-wrap min-w-0 text-xs"
			data-testid="season-context"
		>
			{range && (
				<span className="text-muted-foreground font-mono text-[11px] shrink-0">
					{formatRange(range, language)}
				</span>
			)}
			{phases.map((phase) => (
				<span
					key={phase.mesocycle_id}
					className="inline-flex items-center gap-1 rounded-sm border px-1.5 h-5 text-[10px] font-heading uppercase tracking-wider truncate max-w-[180px]"
					style={{
						borderColor: `${readable(phase.color || "#007AFF")}80`,
						color: readable(phase.color || "#007AFF"),
					}}
					title={phase.description || phase.name}
				>
					{phase.name}
				</span>
			))}
			{next && (
				<span
					className={`inline-flex items-center gap-1 rounded-sm px-1.5 h-5 text-[10px] font-medium truncate max-w-[260px] ${
						nextInWeek || next.priority === "A"
							? "bg-blaze/15 text-blaze"
							: "bg-secondary text-muted-foreground"
					}`}
					title={competitionLabel(next, agenda?.athleteCount, t)}
				>
					<Trophy className="w-3 h-3 shrink-0" />
					<span className="truncate">{next.name}</span>
					<span className="shrink-0 opacity-80">
						· {formatCountdown(daysToNext, t)}
					</span>
				</span>
			)}
			{agenda && !next && agenda.showPhases && phases.length > 0 && (
				<span className="text-muted-foreground text-[10px]">
					{t("pbSeasonNoCompetitions")}
				</span>
			)}
			{showSubjectPicker && (
				<Select value={selectedSubjectId || ""} onValueChange={onSelectSubject}>
					<SelectTrigger
						className="h-6 w-auto max-w-[180px] rounded-sm border-border bg-transparent px-2 text-[11px] gap-1"
						aria-label={t("pbSeasonAthleteSelector")}
					>
						<SelectValue />
					</SelectTrigger>
					<SelectContent className="bg-card border-border">
						{subjectOptions.map((athlete) => (
							<SelectItem key={athlete.user_id} value={athlete.user_id}>
								{athlete.name}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			)}
			{agenda && (
				<Button
					type="button"
					variant="ghost"
					size="sm"
					onClick={onPlan}
					className="h-6 px-2 rounded-sm text-[11px] text-volt hover:bg-volt/10"
					title={agenda.showPhases ? t("pbSeasonPlanAthleteHint") : t("pbSeasonPlanGroupHint")}
					data-testid="season-plan"
				>
					{t("pbSeasonPlan")}
					<ExternalLink className="w-3 h-3 ml-1" />
				</Button>
			)}
		</div>
	);
}
